import { RootState, store } from '@/plugins/store';
import { ActionTree } from 'vuex';
import { HomeAction, HomeActionTypes } from './actions-types';
import {
    BILLING_WIDGET,
    ESTIMATE_WIDGET,
    HOME,
    INTERVENTION_REQUEST_WIDGET,
    PREVENTIVE_OUT_WIDGET,
    PREVENTIVE_WIDGET
} from './mutation-types';
import { HomeState, Langue } from './state';
import HomeService from '../services/HomeService';
import ActualitesService from '@/modules/Actualites/services/ActualitesService';
import { Actualite } from '@/models/Actualite';
import i18n from '@/plugins/i18n';
import StringUtils from '@/common/utils/StringUtils';
import { Dashboard } from '@/models/Dashboard';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import ErrorManager from '@/common/utils/ErrorManager';
import { Widget } from '@/models/Widget';
import { SELECTED_DASHBOARD_KEY } from './mutations';

export const actions: ActionTree<HomeState, RootState> = {

    async [HomeAction.SHOW_HOMEPAGE_MODAL]({commit}: HomeActionTypes): Promise<void> {
        const response = await HomeService.getCommunication();
        if (response.status === 200 && response.data !== undefined) {
            commit(HOME.SET_HOMEPAGE_COMMUNICATION, response.data);
        }
    },
    async [HomeAction.SEEN](HomeActionTypes, payload: string): Promise<void> {
        HomeService.setCommunicationVue(payload);
    },
    async [HomeAction.DONT_SHOW]({commit}: HomeActionTypes, payload: string): Promise<void> {
        HomeService.setCommunicationNePlusAfficher(payload);
    },
    async [HomeAction.KNOW_MORE]({commit}: HomeActionTypes, payload: string): Promise<void> {
        HomeService.setCommunicationEnSavoirPlus(payload);
    },
    async [HomeAction.CONTACT_ME]({commit}: HomeActionTypes, payload: string): Promise<void> {
        HomeService.setCommunicationEtreContacte(payload);
    },
    async [HomeAction.RESET_MODULES]({commit}: HomeActionTypes): Promise<void> {
        commit(`widgetFactures/${BILLING_WIDGET.RESET}`);
        commit(`widgetDevis/${ESTIMATE_WIDGET.RESET}`);
        commit(`widgetDemandeIntervention/${INTERVENTION_REQUEST_WIDGET.RESET}`);
        commit(`widgetTravaux/${PREVENTIVE_OUT_WIDGET.RESET}`);
        commit(`widgetPreventif/${PREVENTIVE_WIDGET.RESET}`);
    },
    async [HomeAction.GET_ACTUALITES]({commit}: HomeActionTypes): Promise<void> {
        commit(HOME.SET_ACTUALITE_LOADING, true);
        const response = await ActualitesService.getActualites();
        if (response.status === 200 && response.data !== undefined) {
            commit(HOME.SET_ACTUALITES, response.data);
            let nbNouvelleActualite = 0;
            if (response.data != null) {
                response.data.forEach((actualite: Actualite) => {
                    if (!actualite.dejaConsulte) {
                        nbNouvelleActualite++;
                    }
                });
            }
            commit(HOME.SET_NB_NOUVELLES_ACTUS, nbNouvelleActualite);
        }
        commit(HOME.SET_ACTUALITE_LOADING, false);
    },
    async [HomeAction.SET_ACTUALITE_VUE]({commit}: HomeActionTypes, actualiteId: number): Promise<void> {
        await ActualitesService.setActualiteConsulte(actualiteId);
    },
    [HomeAction.SET_ACTUALITE_OPEN]({commit}: HomeActionTypes, actualiteOpen: boolean): void {
        commit(HOME.SET_ACTUALITE_OPEN, actualiteOpen);
    },
    [HomeAction.GET_NB_NOUVELLES_RECLAMATIONS]({ commit }: HomeActionTypes ): void {
        HomeService.getNbNouvellesReclamations().then((response: any) => {
            commit(HOME.SET_NB_NOUVELLES_RECLAMATIONS, response.data);
        });
    },
    [HomeAction.GET_LANGUES]({ commit, getters }: HomeActionTypes): void {
        if (getters.getLanguesDisponibles.length === 0) {
            HomeService.getLangues().then((response: any) => {
                commit(HOME.SET_LANGUES_DISPONIBLES, response.data);
            });
        }
    },
    [HomeAction.GET_LANGUE_SELECTIONNEE]({ commit, getters }: HomeActionTypes ): Langue | undefined {
        if (getters.getLangueSelectionnee == null) {
            HomeService.getLangueSelectionnee().then((response: any) => {
                commit(HOME.SET_LANGUE_SELECTIONNEE, response.data);
                return response.data;
            });
        }
        return undefined;
    },
    [HomeAction.SET_LANGUE]({ commit }: HomeActionTypes, langueId: number ): void {
        HomeService.setLangue(langueId).then((response: any) => {
            commit(HOME.SET_LANGUE_SELECTIONNEE, response.data);
        });
    },
    async [HomeAction.GET_TRADUCTIONS]({ commit }: HomeActionTypes ): Promise<any> {
        let traductions;
        await HomeService.getTraductions().then(async (response: any) => {
            commit(HOME.SET_TRADUCTIONS, response.data);
            traductions = response.data;
            let langue: Langue = await store.getters.getLangueSelectionnee;
            if (langue == undefined) {
                await store.dispatch(HomeAction.GET_LANGUE_SELECTIONNEE).then((response: Langue) => {
                    langue = response;
                });
            }
            i18n.global.locale = langue.locale;
            traductions = StringUtils.buildi18nMessages(traductions);
            i18n.global.setLocaleMessage(langue.locale, traductions);
        });
        return traductions;
    },

    [HomeAction.GET_WIDGETS_DISPONIBLES]({ commit }: HomeActionTypes ): void {
        HomeService.getWidgets().then((response: any) => {
            commit(HOME.SET_WIDGETS, response.data);
        });
    },
    async [HomeAction.GET_DASHBOARDS]({ commit, getters }: HomeActionTypes): Promise<Dashboard[]> {
        try {
            commit(HOME.SET_DASHBOARD_LOADING, true);
            const response = await HomeService.getDashboards();
            commit(HOME.SET_DASHBOARDS, response.data);
            const idSelectionne: number | undefined =
                getters.getSelectedDashboard?.id ?? Number(sessionStorage.getItem(SELECTED_DASHBOARD_KEY) ?? NaN);
            const dashboardSelectionne = response.data.find((dashboard: Dashboard) => dashboard.id === idSelectionne);

            let dashboardfavori = undefined;
            response.data.forEach((dashboard: Dashboard) => {
                if (dashboard.favori) {
                    dashboardfavori = dashboard;
                }
            });

            if (dashboardSelectionne !== undefined) {
                commit(HOME.SET_SELECTED_DASHBOARD, dashboardSelectionne);
            } else if (dashboardfavori !== undefined) {
                commit(HOME.SET_SELECTED_DASHBOARD, dashboardfavori);
            } else {
                commit(HOME.SET_SELECTED_DASHBOARD, response.data[0]);
            }

            return response.data;
        } catch (error) {
            commit(
                `errorToaster/${ERROR.SET_MESSAGE}`,
                await ErrorManager.processError(error),
                { root: true }
            );
            throw error;
        } finally {
            commit(HOME.SET_DASHBOARD_LOADING, false);
        }
    },
    [HomeAction.GET_DASHBOARD]({ commit }: HomeActionTypes, id: number): Promise<Dashboard> {
        return new Promise((resolve, reject) => {
            HomeService.getDashboard(id).then((response: any) => {
                commit(HOME.SET_SELECTED_DASHBOARD, response.data);
                resolve(response.data);
            }).catch(async error =>{
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processError(error), { root: true });
                reject();
            });
        });
    },
    async [HomeAction.SET_DASHBOARD]({ commit }: HomeActionTypes, dashboard: Dashboard): Promise<Dashboard | undefined> {
        commit(HOME.SET_ENREGISTREMENT_MODALE_EN_COURS, true);
        await HomeService.postDashboard(dashboard).then(async (response: any) => {
            if (response.status === 200 && response.data !== undefined) {
                commit(HOME.SET_SELECTED_DASHBOARD, response.data);
                commit(HOME.SET_CREATED_DASHBOARD, response.data);
                return response.data;
            } else {
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                    { root: true }
                );
            }
        }).catch(async error =>{
            commit(
                `errorToaster/${ERROR.SET_MESSAGE}`,
                await ErrorManager.processError(error), { root: true });
        });
        commit(HOME.SET_ENREGISTREMENT_MODALE_EN_COURS, false);
        return undefined;
    },
    async [HomeAction.PUT_DASHBOARD]({ commit }: HomeActionTypes, dashboard: Dashboard): Promise<Dashboard | undefined> {
        commit(HOME.SET_ENREGISTREMENT_EN_COURS, true);
        await HomeService.putDashboard(dashboard).then(async (response: any) => {
            if (response.status === 200 && response.data !== undefined) {
                commit(HOME.SET_SELECTED_DASHBOARD, response.data);
                return response.data;
            } else {
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                    { root: true }
                );
            }
        }).catch(async error =>{
            commit(
                `errorToaster/${ERROR.SET_MESSAGE}`,
                await ErrorManager.processError(error), { root: true });
        });
        commit(HOME.SET_ENREGISTREMENT_EN_COURS, false);
        return undefined;
    },
    async [HomeAction.DELETE_DASHBOARD]({ commit }: HomeActionTypes, dashboard: Dashboard): Promise<Dashboard | undefined> {
        await HomeService.deleteDashboard(dashboard).then(async (response: any) => {
            commit(HOME.SET_ENREGISTREMENT_EN_COURS, true);
            if (response.status === 200 && response.data !== undefined) {
                return response.data;
            } else {
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                    { root: true }
                );
            }
        }).catch(async error =>{
            commit(
                `errorToaster/${ERROR.SET_MESSAGE}`,
                await ErrorManager.processError(error), { root: true });
        });
        commit(HOME.SET_ENREGISTREMENT_EN_COURS, false);
        return undefined;
    },
    async [HomeAction.CHANGE_WIDGET_POSITION]({ commit }: HomeActionTypes, item: {widget: Widget, nouvellePosition: number}): Promise<Dashboard | undefined> {
        commit(HOME.SET_ENREGISTREMENT_EN_COURS, true);
        await HomeService.changeWidgetPosition(item.widget, item.nouvellePosition).then(async (response: any) => {
            if (response.status === 200 && response.data !== undefined) {
                return response.data;
            } else {
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                    { root: true }
                );
            }
        }).catch(async error =>{
            commit(
                `errorToaster/${ERROR.SET_MESSAGE}`,
                await ErrorManager.processError(error), { root: true });
        });
        commit(HOME.SET_ENREGISTREMENT_EN_COURS, false);
        return undefined;
    },
    async [HomeAction.POST_WIDGET]({ commit }: HomeActionTypes, widget: Widget): Promise<Widget | undefined> {
        commit(HOME.SET_ENREGISTREMENT_MODALE_EN_COURS, true);
        await HomeService.postWidget(widget).then(async (response: any) => {
            if (response.status === 200 && response.data !== undefined) {
                return response.data;
            } else {
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                    { root: true }
                );
            }
        }).catch(async error =>{
            commit(
                `errorToaster/${ERROR.SET_MESSAGE}`,
                await ErrorManager.processError(error), { root: true });
        });
        commit(HOME.SET_ENREGISTREMENT_MODALE_EN_COURS, false);
        return undefined;
    },
    async [HomeAction.DELETE_WIDGET]({ commit }: HomeActionTypes, widget: Widget): Promise<Widget | undefined> {
        if (widget.id === undefined) {
            // todo gestion erreur
            return undefined;
        }
        commit(HOME.SET_ENREGISTREMENT_EN_COURS, true);

        await HomeService.deleteWidget(widget.id).then(async (response: any) => {
            if (response.status === 200 && response.data !== undefined) {
                return response.data;
            } else {
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                    { root: true }
                );
            }
        }).catch(async error =>{
            commit(
                `errorToaster/${ERROR.SET_MESSAGE}`,
                await ErrorManager.processError(error), { root: true });
        });
        commit(HOME.SET_ENREGISTREMENT_EN_COURS, false);

        return undefined;
    },
};
